import { Dispatch, SetStateAction } from 'react';

import { prepareErrorMessage } from '#/features/wallet/ethereum';
import { logException } from '#/features/logging/logging';
import {
  AccountContractConfigurable,
  EthereumChainConfigurable,
  WalletState,
} from '#/features/wallet/wallet-context';
import { buildStarkKeysTypedData } from '#/features/wallet/chain';
import StarknetWallet from '#/features/starknet-wallet/wallet';
import { AuthActions, AuthState } from '#/features/auth/auth-context';
import EthereumWallet from '#/features/ethereum-wallet/wallet';

import { AsyncResult } from '#/utils/types';
import { getStorageItem } from '#/utils/localStorage';
import { getIsEthereumWalletCompliantStorageKey } from '#/features/storage/storage';

export default function actionInitializeStarknetWallet(
  ...params: Parameters<typeof initializeStarknetWallet>
) {
  return async () => initializeStarknetWallet(...params);
}

async function initializeStarknetWallet(
  authState: AuthState,
  authActions: AuthActions,
  setWalletState: Dispatch<SetStateAction<WalletState>>,
  options: EthereumChainConfigurable & AccountContractConfigurable,
): AsyncResult<void> {
  const actions = prepareActions(setWalletState);

  actions.resetError();
  actions.setStatus('signing_stark_keys');

  try {
    await EthereumWallet.switchChainId(options.ethereumChainId);
  } catch (err) {
    const description = 'Error switching Ethereum chain';
    const { message, isException } = prepareErrorMessage(description, err);
    actions.setError(message);
    actions.setStatus('wallet_connected_idle');
    if (isException) {
      const error = new Error(description, { cause: err });
      logException(error);
      return { ok: false, error };
    }
    return { ok: false, error: null };
  }

  try {
    const isSeedAvailable = authState.walletSeed != null;
    const isSeedEthereumAddressAvailable = authState.ethereumAddress != null;

    if (isSeedAvailable && !isSeedEthereumAddressAvailable) {
      throw new Error(
        `Starknet Wallet seed was recovered, but there is no associated ethereum address`,
      );
    }

    const retrieveEthereumAddress = () => {
      if (isSeedEthereumAddressAvailable) {
        return authState.ethereumAddress;
      }
      return EthereumWallet.getAddress();
    };
    const retrieveSignature = async () => {
      if (isSeedAvailable) {
        return authState.walletSeed;
      }
      const starkKeysTypedData = buildStarkKeysTypedData(
        options.ethereumChainId,
      );
      // @ts-expect-error `domain.chainId` "number" not assignable to "bigint", but for correct signature the `chainId` should be of type "number"
      return EthereumWallet.signTypedData(starkKeysTypedData);
    };
    const verifyDeterministicSigning = async (ethereumAddress: string) => {
      const storageKey =
        getIsEthereumWalletCompliantStorageKey(ethereumAddress);
      const isEthereumWalletIsCompliant =
        getStorageItem<boolean>(storageKey) ?? false;

      if (isEthereumWalletIsCompliant) return true;

      const additionalSignature = await retrieveSignature();
      if (signature !== additionalSignature) return false;

      return true;
    };

    const ethereumAddress = retrieveEthereumAddress();
    const signature = await retrieveSignature();
    const isDeterministicSigning = await verifyDeterministicSigning(
      ethereumAddress,
    );

    if (!isDeterministicSigning) {
      throw new Error(
        `Your wallet does not support deterministic signing. Please use different wallet.`,
      );
    }

    StarknetWallet.initializeAccount(
      ethereumAddress,
      signature,
      options.accountClassHash,
      options.accountProxyClassHash,
    );

    const address = StarknetWallet.getAddress();
    actions.setStarknetAddress(address);

    if (authState.rememberMe) {
      authActions.rememberUser(ethereumAddress, signature);
    }
  } catch (err) {
    const description = 'Failed to derive Starknet Private Key';
    const { message, isException } = prepareErrorMessage(description, err);
    actions.setError(message);
    actions.setStatus('wallet_connected_idle');
    if (isException) {
      const error = new Error(description, { cause: err });
      return { ok: false, error };
    }
    return { ok: false, error: null };
  }

  actions.setStatus('wallet_connected_idle');
  return { ok: true, data: undefined };
}

function prepareActions(setWalletState: Dispatch<SetStateAction<WalletState>>) {
  function setStatus(status: WalletState['step']) {
    setWalletState((state) => ({ ...state, step: status }));
  }

  function setStarknetAddress(starknetAddress: WalletState['starknetAddress']) {
    setWalletState((state) => ({ ...state, starknetAddress }));
  }

  const setError = (error: string) => {
    setWalletState((state) => ({ ...state, error }));
  };

  const resetError = () => {
    setWalletState((state) => ({ ...state, error: '' }));
  };

  return {
    setStatus,
    setStarknetAddress,
    setError,
    resetError,
  };
}
